// src/pages/CommentsPage.js
import React, { useState, useEffect, useMemo } from "react";
import { Container, Table, Button, Modal, Form } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import { ToastContainer, toast } from "react-toastify";

import EditComment from "./EditComment";

const CommentsPage = () => {
  const [comments, setComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editComment, setEditComment] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredComment = useMemo(() => {
    return comments.filter((row) => {
      return (
        row.ac_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.com_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.formatted_date.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (row.com_status === 0 &&
          "pending".includes(searchTerm.toLowerCase())) ||
        (row.com_status === 1 &&
          "approved".includes(searchTerm.toLowerCase())) ||
        (row.com_status === 2 &&
          "rejected".includes(searchTerm.toLowerCase())) ||
        (row.com_status === 3 && "spam".includes(searchTerm.toLowerCase()))
      );
    });
  }, [comments, searchTerm]);

  useEffect(() => {
    fetchAllComment();
  }, []);

  const fetchAllComment = async () => {
    const response = await axios.post(BASE_URL + "API/apicall", {
      security_key: security_key,
      mode: "getAllComments",
    });

    const output = response.data;

    if (output.length > 0) {
      setComments(output);
    }
  };

  // Handlers
  const handleView = (pk_com_key, index) => {
    const comment = comments.find((com) => com.pk_com_key === pk_com_key);
    setSelectedComment(comment);
    setShowViewModal(true);
    setSelectedIndex(Number(index) + 1);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedIndex(null);
  };

  // const handleModerate = (pk_com_key, newStatus) => {
  //   setComments(
  //     comments.map((cmt) =>
  //       cmt.pk_com_key === pk_com_key ? { ...cmt, status: newStatus } : cmt
  //     )
  //   );
  // };
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(comments.length / ITEMS_PER_PAGE);

  const paginatedComments = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return comments.slice(start, end);
  }, [comments, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleModerate = async (pk_com_key, newStatus) => {
    try {
      const response = await axios.post(`${BASE_URL}API/apicall`, {
        security_key: security_key,
        mode: "updateCommentStatus",
        pk_com_key,
        com_status: newStatus,
      });

      if (response.data === 1) {
        setComments(
          comments.map((cmt) =>
            cmt.pk_com_key === pk_com_key
              ? { ...cmt, com_status: newStatus }
              : cmt
          )
        );
        toast.success("Status updated successfully!");
      }
    } catch (error) {
      toast.error("Error occurred while updating status.");
      console.error(error);
    }
  };

  const handleDelete = async (com_key) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        const response = await axios.post(BASE_URL + "API/apicall", {
          security_key: security_key,
          mode: "deleteComment",
          pk_com_key: com_key,
        });

        const output = response.data;

        if (output) {
          await Swal.fire(
            "Deleted!",
            "The Comment has been successfully removed.",
            "success"
          );
          fetchAllComment();
        }
      }
    } catch (error) {
      // Handle any errors here
      console.error("An error occurred: ", error);
    }
  };

  const handleEdit = async (comment) => {
    setEditComment(comment);
    setShowEditModal(true);
  };

  return (
    <Container className="mt-4">
      <ToastContainer position="top-center" />
      <div className="border-0">
        <h4 className="border-0 mb-2">Comments Management</h4>

        <div className="mb-4">
          <Form>
            <div className="row">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Search by name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </div>

        <div className="border-0 bg-light">
          <div className="table-responsive">
            <Table
              responsive
              striped
              bordered
              hover
              className="table-responsive-scroll mb-0"
            >
              <thead className="thead-dark">
                <tr>
                  <th>ID</th>
                  <th>Article Name</th>
                  <th>User</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th style={{ width: "110px" }}>Date</th>
                  <th style={{ width: "380px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedComments.map((comment, index) => (
                  <tr key={comment.pk_com_key}>
                    <td>{index + 1}</td>
                    <td>{comment.ac_name}</td>
                    <td>{comment.com_name}</td>
                    <td>{comment.comment}</td>
                    <td
                      style={{
                        color:
                          comment.com_status === 1
                            ? "green"
                            : comment.com_status === 2
                            ? "red"
                            : "black",
                      }}
                    >
                      {comment.com_status === 0 && "Pending"}
                      {comment.com_status === 1 && "Approved"}
                      {comment.com_status === 2 && "Rejected"}
                      {comment.com_status === 3 && "Spam"}
                    </td>
                    <td>{comment.formatted_date}</td>
                    <td>
                      <div className="actbtn-grp">
                        <Button
                          variant="outline-dark"
                          onClick={() => handleEdit(comment)}
                          className="me-2"
                          data-bs-toggle="tooltip"
                          title="Edit"
                        >
                          <i className="fa-solid fa-pen-to-square"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleDelete(comment.pk_com_key)}
                          className="me-2"
                          data-bs-toggle="tooltip"
                          title="Delete"
                        >
                          {" "}
                          <i className="fa-solid fa-trash"></i>
                        </Button>
                        <Button
                          variant="outline-info"
                          onClick={() => handleView(comment.pk_com_key)}
                          className="me-2"
                          data-bs-toggle="tooltip"
                          title="View"
                        >
                          <i className="fa-solid fa-eye"></i>
                        </Button>
                        <Button
                          variant="outline-success"
                          onClick={() => handleModerate(comment.pk_com_key, 1)} // Approve
                          className="me-2"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleModerate(comment.pk_com_key, 2)} // Reject
                          className="me-2"
                        >
                          Reject
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => handleModerate(comment.pk_com_key, 3)} // Mark as Spam
                        >
                          Mark as Spam
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="pagination d-flex justify-content-end  my-3">
          <Button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-light mx-1"
          >
            &lt;&lt;
          </Button>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-light mx-1"
          >
            &lt;
          </Button>
          {Array.from({ length: totalPages }, (_, index) => (
            <Button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`mx-1 ${
                currentPage === index + 1 ? "btn-dark text-white" : "btn-light"
              }`}
            >
              {index + 1}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-light mx-1"
          >
            &gt;
          </Button>
          <Button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-light mx-1"
          >
            &gt;&gt;
          </Button>
        </div>
        <EditComment
          showModal={showEditModal}
          handleClose={handleCloseEditModal}
          fetchAllComment={fetchAllComment}
          comment={editComment}
        />
      </div>

      {/* View Modal */}
      {selectedComment && (
        <Modal show={showViewModal} onHide={handleCloseViewModal}>
          <Modal.Header closeButton>
            <Modal.Title>Comment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* <p>
              <strong>ID:</strong> {selectedComment.id}
            </p> */}
            <p>
              <strong>Article Name:</strong> {selectedComment.ac_name}
            </p>
            <p>
              <strong>User:</strong> {selectedComment.com_name}
            </p>
            <p>
              <strong>Text:</strong> {selectedComment.comment}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              {selectedComment.com_status === 0 && "Pending"}
              {selectedComment.com_status === 1 && "Approved"}
              {selectedComment.com_status === 2 && "Rejected"}
              {selectedComment.com_status === 3 && "Spam"}
            </p>
            <p>
              <strong>Date:</strong> {selectedComment.formatted_date}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseViewModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <style jsx>{`
        .actbtn-grp {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
      `}</style>
    </Container>
  );
};

export default CommentsPage;
